import React, { useEffect, useRef, useState } from "react";
import NavigationBar from "../components/ui/Navbar";
import { useParams } from "react-router-dom";
import { UserAuth } from "../lib/authContext";
import CreatePost from "../components/modals/CreatePost";
import { AllData, Post } from "../lib/types";
import { clienthubApiClient, nexusApiClient } from "../lib/apiClient";
import { Carousel } from "flowbite-react";
import ProfilePicture from "../components/ProfilePicture";
import { shortName, timePosted } from "../lib/funcs";
import { authEmails } from "../lib/constants";
import { toast } from "react-toastify";
import { DownArrow, Settings, UpArrow } from "../assets/Icons";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "../components/ui/dialog";

const Event = () => {
  const { userData, events, getAllEvents } = UserAuth();
  const { event_id } = useParams<{ event_id: string }>();

  const [showSettings, setShowSettings] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [event, setEvent] = useState<AllData>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [showPosts, setShowPosts] = useState(true);
  const [newComment, setNewComment] = useState<{ [key: string]: string }>({});
  const [commentImages, setCommentImages] = useState<{
    [key: string]: File[];
  }>({});
  const [commentAudio, setCommentAudio] = useState<{
    [key: string]: File | null;
  }>({});
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [activeImagePostId, setActiveImagePostId] = useState<string | null>(
    null,
  );
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageMode, setImageMode] = useState<"upload" | "camera">("upload");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoStreamRef = useRef<MediaStream | null>(null);

  const [activeAudioPostId, setActiveAudioPostId] = useState<string | null>(
    null,
  );
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [viewImageUrl, setViewImageUrl] = useState<string | null>(null);
  const [viewImageDialogOpen, setViewImageDialogOpen] = useState(false);
  const [zzeehOrders, setZzeehOrders] = useState<any[]>([]);
  const [showZzeehOrders, setShowZzeehOrders] = useState(true);

  const fetchPosts = async () => {
    try {
      const response = await clienthubApiClient.get(`/event/${event_id}`);
      console.log(response.data);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    }
  };

  const fetchZzeehOrders = async () => {
    try {
      const response = await nexusApiClient.get(`/orders/event/${event_id}`);
      // console.log(response.data);
      setZzeehOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  const stopVideoStream = () => {
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((t) => t.stop());
      videoStreamRef.current = null;
    }
  };

  const openImageDialog = (postId: string) => {
    setActiveImagePostId(postId);
    setImageDialogOpen(true);
    setImageMode("upload");
    stopVideoStream();
  };

  const closeImageDialog = () => {
    setImageDialogOpen(false);
    setActiveImagePostId(null);
    stopVideoStream();
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error("Unable to access camera:", err);
      toast.error("Unable to access camera. Please check permissions.");
    }
  };

  const captureImageFromCamera = () => {
    if (!videoRef.current || !activeImagePostId) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "remark-image.jpg", { type: blob.type });
      setCommentImages((prev) => ({
        ...prev,
        [activeImagePostId]: [file],
      }));
      stopVideoStream();
    }, "image/jpeg");
  };

  const startRecording = async (postId: string) => {
    try {
      setNewComment((prev) => ({ ...prev, [postId]: "" }));
      setCommentImages((prev) => ({ ...prev, [postId]: [] }));
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setActiveAudioPostId(postId);
      setIsRecording(true);
      setRecordedAudioUrl(null);

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setRecordedAudioUrl(url);
        const file = new File([blob], "voice-note.webm", {
          type: blob.type || "audio/webm",
        });
        setCommentAudio((prev) => ({
          ...prev,
          [postId]: file,
        }));
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
      };

      mediaRecorder.start();
    } catch (err) {
      console.error("Unable to access microphone:", err);
      toast.error("Unable to access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  const handleComment = async (postId: string) => {
    try {
      const comment = newComment[postId];
      const imageFiles = commentImages[postId] || [];
      const audioFile = commentAudio[postId] || null;

      if (!comment && imageFiles.length === 0 && !audioFile) return;

      const formData = new FormData();
      if (userData?.user_id) {
        formData.append("user_id", userData.user_id);
      }
      if (comment) {
        formData.append("comment", comment);
      }
      if (imageFiles.length > 0) {
        imageFiles.forEach((file) => {
          formData.append("image", file);
        });
      }
      if (audioFile) {
        formData.append("voice_note", audioFile);
      }

      const response = await clienthubApiClient.post(
        `/${postId}/comment`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("response.data", response.data);
      toast.success("Remark posted successfully!");

      setNewComment({ ...newComment, [postId]: "" });
      setCommentImages({ ...commentImages, [postId]: [] });
      setCommentAudio({ ...commentAudio, [postId]: null });
      setRecordedAudioUrl(null);
      setActiveAudioPostId(null);

      const createdComment =
        response?.data && typeof response.data === "object"
          ? response.data
          : {
              full_name: userData?.full_name || "You",
              comment: comment || "",
              image_url: null,
              audio_url: null,
            };

      setPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.post_id === postId
            ? {
                ...post,
                comments: [...post.comments, createdComment],
              }
            : post,
        ),
      );

      fetchPosts();
    } catch (error) {
      console.error("Failed to post remark:", error);
      toast.success("Failed to post remark. Please try again.");
    }
  };

  useEffect(() => {
    getAllEvents();
  }, []);

  useEffect(() => {
    setEvent(events?.find((event) => event.event_id === event_id));
  }, [getAllEvents]);

  useEffect(() => {
    if (event_id) {
      fetchPosts();
      fetchZzeehOrders();
    }
  }, [event_id]);

  return (
    <div className="flex flex-col items-center">
      <NavigationBar />

      <div
        id="event"
        className="flex flex-col min-h-screen py-28 px-3 container"
      >
        <div className="flex flex-row items-center justify-between">
          <div>
            <p className="text-2xl md:text-3xl font-medium">
              {event?.event_name}
            </p>
            <p className="text-sm md:text-base text-gray-500">
              {event && event.start_date && event.end_date
                ? `${new Date(event.start_date).toDateString()} - ${new Date(
                    event.end_date,
                  ).toDateString()}`
                : "Date not available"}
            </p>
          </div>

          {userData?.email && authEmails.includes(userData.email) && (
            <button
              className="bg-white rounded-full shadow p-3 hover:animate-spin"
              onClick={() => setShowSettings((prev) => !prev)}
            >
              <Settings />
            </button>
          )}
        </div>

        {showSettings && (
          <div className="flex flex-col md:flex-row gap-2 mt-5">
            <button
              className="text-center select-none text-black bg-gradient-to-b from-[#fce96a] to-[#e3a008] rounded-md focus:outline-none hover:from-yellow-500 hover:to-yellow-400 p-2"
              onClick={() =>
                window.open("https://zzeeh-nexus.vercel.app", "_blank")
              }
            >
              <p>Create Zzeeh Order</p>
            </button>
            <button
              className="text-center select-none text-black bg-gradient-to-b from-[#fce96a] to-[#e3a008] rounded-md focus:outline-none hover:from-yellow-500 hover:to-yellow-400 p-2"
              onClick={() => setModalVisible(true)}
            >
              <p>Create Post</p>
            </button>
          </div>
        )}

        <div
          className="flex items-center mt-12 mb-3"
          onClick={() => setShowPosts(!showPosts)}
        >
          <p className="text-xl font-semibold text-gold uppercase">Posts</p>
          <button
            className="text-gray-600 hover:text-black transition ml-2"
            title={showPosts ? "Hide Posts" : "Show Posts"}
          >
            {showPosts ? <UpArrow /> : <DownArrow />}
          </button>
        </div>

        {posts.length > 0 ? (
          showPosts && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post, i) => (
                <div key={i} className="border rounded-xl bg-white shadow">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <ProfilePicture username={post.posted_by_name} />
                      <div>
                        <p className="font-medium">{post.posted_by_name}</p>
                        <p className="text-xs text-gray-500">
                          {timePosted(post.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Media */}
                  <div className="w-full h-[500px] bg-black">
                    <Carousel slide={true} indicators={true}>
                      {post.post_image.map((url, idx) =>
                        url.match(/\.(mp4|webm|ogg)$/) ? (
                          <video
                            key={idx}
                            src={url}
                            controls
                            className="w-full max-h-[500px] object-contain bg-black"
                          />
                        ) : (
                          <img
                            key={idx}
                            src={url}
                            alt={`Media ${idx}`}
                            className="w-full max-h-[500px] object-contain bg-black"
                          />
                        ),
                      )}
                    </Carousel>
                  </div>

                  {/* Post Title */}
                  <div className="px-4 py-2">
                    <p className="font-semibold">{post.post_title}</p>
                  </div>

                  {/* Comments */}
                  {post.comments[0].user_id !== null && (
                    <div className="px-4 py-2 space-y-2 text-sm border-t">
                      {post.comments
                        .slice(
                          0,
                          expandedPostId === post.post_id
                            ? post.comments.length
                            : 3,
                        )
                        .map((c, i) => (
                          <div key={i} className="space-y-1">
                            <p className="flex space-x-2">
                              <strong>{shortName(c.full_name)}</strong>
                              <span>{c.comment}</span>
                            </p>
                            {c.images && c.images.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {c.images.map((image, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="h-20 rounded-md overflow-hidden border bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gold/70"
                                    onClick={() => {
                                      setViewImageUrl(image);
                                      setViewImageDialogOpen(true);
                                    }}
                                  >
                                    <img
                                      src={image}
                                      alt="Remark attachment"
                                      className="h-full w-full object-cover pointer-events-none"
                                    />
                                  </button>
                                ))}
                              </div>
                            )}

                            {c.voice_note && (
                              <audio
                                controls
                                className="w-full max-w-xs"
                                src={c.voice_note}
                              />
                            )}
                          </div>
                        ))}
                      {post.comments.length > 3 &&
                        expandedPostId !== post.post_id && (
                          <p
                            className="text-gray-500 text-xs mt-1 cursor-pointer hover:underline"
                            onClick={() => setExpandedPostId(post.post_id)}
                          >
                            View all {post.comments.length} remarks
                          </p>
                        )}
                      {expandedPostId === post.post_id &&
                        post.comments.length > 3 && (
                          <p
                            className="text-gray-500 text-xs mt-1 cursor-pointer hover:underline"
                            onClick={() => setExpandedPostId(null)} // Collapse the comments
                          >
                            View less remarks
                          </p>
                        )}
                    </div>
                  )}

                  {/* Comment Input */}
                  <div className="px-4 py-3 border-t space-y-2 bg-gray-50/60">
                    {commentAudio[post.post_id] ? (
                      <div className="space-y-2">
                        <p className="text-xs text-gray-600 font-medium">
                          Voice note ready to send
                        </p>
                        <audio
                          controls
                          className="w-full max-w-xs"
                          src={recordedAudioUrl || ""}
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRecordedAudioUrl(null);
                              setActiveAudioPostId(null);
                              setCommentAudio((prev) => ({
                                ...prev,
                                [post.post_id]: null,
                              }));
                            }}
                          >
                            Retake
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleComment(post.post_id)}
                          >
                            Send
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="flex-1 bg-white px-3 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-gold/70"
                            placeholder="Add a remark..."
                            value={newComment[post.post_id] || ""}
                            onChange={(e) =>
                              setNewComment({
                                ...newComment,
                                [post.post_id]: e.target.value,
                              })
                            }
                          />
                          <Button
                            size="icon"
                            variant="outline"
                            type="button"
                            onClick={() => openImageDialog(post.post_id)}
                            title="Add image"
                          >
                            📷
                          </Button>
                          <Button
                            size="icon"
                            variant={
                              isRecording && activeAudioPostId === post.post_id
                                ? "default"
                                : "outline"
                            }
                            type="button"
                            onClick={() =>
                              isRecording && activeAudioPostId === post.post_id
                                ? stopRecording()
                                : startRecording(post.post_id)
                            }
                            title={
                              isRecording && activeAudioPostId === post.post_id
                                ? "Stop recording"
                                : "Record voice note"
                            }
                          >
                            {isRecording && activeAudioPostId === post.post_id
                              ? "⏹"
                              : "🎤"}
                          </Button>
                          <Button
                            type="button"
                            size="md"
                            onClick={() => handleComment(post.post_id)}
                          >
                            Send
                          </Button>
                        </div>

                        {isRecording && activeAudioPostId === post.post_id && (
                          <div className="flex items-center gap-2 text-[11px] text-red-600 mt-1">
                            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                            <span>Recording...</span>
                          </div>
                        )}

                        {commentImages[post.post_id] &&
                          commentImages[post.post_id].length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {commentImages[post.post_id].map((file, idx) => (
                                <div
                                  key={idx}
                                  className="h-16 w-16 rounded-md overflow-hidden border bg-gray-100"
                                >
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Selected ${idx + 1}`}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <p>No Posts Found.</p>
        )}

        {/* Orders Section */}
        <div
          className="flex items-center cursor-pointer mt-12 mb-3"
          onClick={() => setShowZzeehOrders(!showZzeehOrders)}
        >
          <p className="text-xl font-semibold text-gold uppercase">
            Zzeeh Orders
          </p>
          <button
            className="text-gray-600 hover:text-black transition ml-2"
            title={showZzeehOrders ? "Hide Orders" : "Show Orders"}
          >
            {showZzeehOrders ? <UpArrow /> : <DownArrow />}
          </button>
        </div>

        {zzeehOrders.length > 0 ? (
          showZzeehOrders && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {zzeehOrders.map((order, idx) => (
                <div
                  key={idx}
                  className="border rounded-xl bg-white shadow p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 mb-2">
                      Order #{idx + 1}
                    </p>
                    <span
                      className={`text-sm px-2 py-1 rounded-full ${
                        order.order_status === "pending"
                          ? "bg-amber-500 text-yellow-100 hover:bg-amber-600"
                          : "bg-green-500 hover:bg-green-600 text-green-100"
                      }`}
                    >
                      {order.order_status === "pending"
                        ? "Pending"
                        : "Completed"}
                    </span>
                  </div>

                  <div className="text-sm space-y-1">
                    <ol className="list-decimal list-inside pt-2">
                      {order.items.map((item: any, i: number) => (
                        <li key={i}>
                          <span className="font-medium">
                            {item.item_name} ({item.reserved_quantity})
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <p>No Orders Found.</p>
        )}
      </div>

      <CreatePost
        open={modalVisible}
        onClose={() => setModalVisible(false)}
        fetchPosts={fetchPosts}
      />

      {/* Image attachment dialog */}
      <Dialog
        open={imageDialogOpen}
        onOpenChange={(open) => !open && closeImageDialog()}
      >
        <DialogOverlay />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add image to remark</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex gap-2 border rounded-full p-1 bg-gray-50 text-xs font-medium w-fit">
              <button
                type="button"
                className={`px-3 py-1 rounded-full transition ${
                  imageMode === "upload"
                    ? "bg-white shadow text-gray-900"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                onClick={() => {
                  setImageMode("upload");
                  stopVideoStream();
                }}
              >
                Upload
              </button>
              <button
                type="button"
                className={`px-3 py-1 rounded-full transition ${
                  imageMode === "camera"
                    ? "bg-white shadow text-gray-900"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                onClick={() => {
                  setImageMode("camera");
                  startCamera();
                }}
              >
                Camera
              </button>
            </div>

            {imageMode === "upload" && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Choose one or more images from your device to add to this
                  remark.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = e.target.files
                      ? Array.from(e.target.files)
                      : [];
                    if (!files.length || !activeImagePostId) return;
                    setCommentImages((prev) => ({
                      ...prev,
                      [activeImagePostId]: [
                        ...(prev[activeImagePostId] || []),
                        ...files,
                      ],
                    }));
                  }}
                />
                {activeImagePostId &&
                  commentImages[activeImagePostId] &&
                  commentImages[activeImagePostId].length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {commentImages[activeImagePostId].map((file, idx) => (
                        <div
                          key={idx}
                          className="h-16 w-16 rounded-md overflow-hidden border bg-gray-100"
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Selected ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}

            {imageMode === "camera" && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500">
                  Grant camera access, frame your shot, and click capture.
                </p>
                {(!activeImagePostId ||
                  !commentImages[activeImagePostId] ||
                  commentImages[activeImagePostId].length === 0) && (
                  <video
                    ref={videoRef}
                    className="w-full rounded-lg bg-black max-h-64 object-contain"
                    autoPlay
                    playsInline
                  />
                )}
                {activeImagePostId &&
                  commentImages[activeImagePostId] &&
                  commentImages[activeImagePostId].length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      {commentImages[activeImagePostId].map((file, idx) => (
                        <div
                          key={idx}
                          className="h-16 w-16 rounded-md overflow-hidden border bg-gray-100"
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Captured ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                {(!activeImagePostId ||
                  !commentImages[activeImagePostId] ||
                  commentImages[activeImagePostId].length === 0) && (
                  <Button
                    type="button"
                    size="md"
                    onClick={captureImageFromCamera}
                    className="w-full"
                  >
                    Capture
                  </Button>
                )}
                {activeImagePostId &&
                  commentImages[activeImagePostId] &&
                  commentImages[activeImagePostId].length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setCommentImages((prev) => ({
                          ...prev,
                          [activeImagePostId]: [],
                        }));
                        startCamera();
                      }}
                    >
                      Retake
                    </Button>
                  )}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={closeImageDialog}
            >
              Close
            </Button>
            <Button
              size="sm"
              type="button"
              disabled={
                !activeImagePostId ||
                !commentImages[activeImagePostId] ||
                commentImages[activeImagePostId].length === 0
              }
              onClick={closeImageDialog}
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View remark image dialog */}
      <Dialog
        open={viewImageDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setViewImageDialogOpen(false);
            setViewImageUrl(null);
          }
        }}
      >
        <DialogOverlay />
        <DialogContent className="max-w-2xl">
          {/* <DialogHeader>
            <DialogTitle>Remark image</DialogTitle>
          </DialogHeader> */}
          <DialogBody>
            {viewImageUrl && (
              <img
                src={viewImageUrl}
                alt="Remark full view"
                className="w-full max-h-[70vh] object-contain rounded-lg bg-black"
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                setViewImageDialogOpen(false);
                setViewImageUrl(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Event;
